// クラブのマップ定義 (データ駆動)。視点モード (俯瞰/iso) に依存しない論理座標系。
// 値だけ変えれば物理 / 音響 / 描画が全部追従する設計。

export type Shape = { type: "rect"; x: number; y: number; w: number; h: number };

export interface ZoneAudio {
  /** 0..1.5 (1 が等倍) */
  gain: number;
  /** 18000 で実質スルー、低くするほどこもる */
  lowpassHz: number;
  /** 0..1 (convolver send 量) */
  reverbWet: number;
}

export interface Speaker {
  id: string;
  /** スピーカースタックの足元 (= screen 描画の基準点)。x は左右壁寄り、y は床中央〜底寄り。 */
  x: number;
  y: number;
  label?: string;
  /** false なら scene.ts の drawSpeakers が描画を skip (= audio-only)。
   *  D1 で追加した壁側 BL/BR/C のような fan-out 用 virtual speaker は、
   *  L/R 巨大スタックとして描かれると bar / DJ booth / screen と被るので、
   *  ここで描画を抑制する (Game review HIGH 対応)。 */
  visible?: boolean;
}

export interface Screen {
  /** 動画 / 静止画を流す中央上部のスクリーン。論理座標は左上 (= obstacle と同じ規約)。 */
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  label?: string;
}

export interface Obstacle {
  x: number;
  y: number;
  w: number;
  h: number;
  color?: number;
  /** scene.ts が描画する「前面」装飾 (= side-view 感を出す darker 帯) の高さ (px)。
   *  未指定なら scene の FRONT_DEPTH (= 18) を使う。壁を「より立体的に高く」見せたい
   *  obstacle (= floor↔bar wall) で 60 程度に上げると、壁の前面が深く見えて
   *  カウンター越しに「奥に立つ壁」感が出る。当たり判定 (= 物理 body) には影響しない
   *  ので、ゲームプレイ上の動線は変わらない。 */
  frontDepth?: number;
}

export interface Zone {
  name: string;
  shape: Shape;
  audio: ZoneAudio;
  visual?: { fill: number; alpha: number; label?: string };
}

export interface Ambience {
  /** ConvolverNode 用 IR。省略時は IIR で代替 or wet 強制 0 */
  irUrl?: string;
  airAbsorption: boolean;
  airAbsorptionRef: number;
  airAbsorptionMaxHz: number;
  airAbsorptionMinHz: number;
  crowdLowCut: { perPersonDb: number };
  /** scene が `setActiveZone(floor)` を呼ぶ前の数フレームに使う wetSend.gain の初期値。
   *  zones のうち最も広い (= floor) の reverbWet と揃えるか、少し小さめの値を入れる。
   *  省略時は 0 (= 旧挙動: scene が tick するまで wet がドライ) */
  defaultReverbWet?: number;
}

export interface NPC {
  /** Phaser texture key (= AVATARS のいずれかと一致させて preload 済の画像を使う)。
   *  存在しない key を渡すと NPC は描画されない (= サイレント skip)。 */
  id: string;
  /** 論理座標 (= 足元基準。avatar sprite は origin (0.5, 1.0) で描画される) */
  x: number;
  y: number;
  /** AVATARS の key */
  avatarKey: string;
  /** 頭上に出すラベル (= 役職名や名前)。省略可 */
  label?: string;
}

export interface Decoration {
  /** 装飾種別。
   *  - "glass": バーカウンター上のグラス (装飾、当たり判定なし)
   *  - "arrow_up": 床に書いた上向き矢印 (= 通路で進行方向を示す pictogram、
   *    迷子防止用)。当たり判定なし、obstacle/sprite より低 depth で描画。
   *  - "bottle": バーの背面 (= floor 側の壁付近) に並ぶ酒瓶。当たり判定なし、
   *    色は x,y からの決定論的 hash で 6 種のパレットを循環。
   *  - "sub_speaker": dance floor 後方 (bar 側壁前) の 80×60 サブウーファー。
   *    Paradise Garage 流。低域 energy で同心円 ring が広がる reactive wave effect の
   *    発信源。obstacles 側に同寸の物理 body を別エントリで持たせて「歩いて通れない」
   *    実体物にする (= decoration 自体は描画のみ、当たり判定は obstacles で)。
   *  - "tweeter_array": 中央上部に天井から吊り下げた小型 horn 5 個の弧状配列。
   *    高域 energy で halo+spark pulse が出る。depth は MAP.world.h + 100 で
   *    player figure より絶対に手前 (= 天井から吊られて下を通っても見える)。 */
  type: "glass" | "arrow_up" | "bottle" | "sub_speaker" | "tweeter_array";
  /** 論理座標 */
  x: number;
  y: number;
}

/** 音響的に最適な聴取位置 (= sweet spot)。`computeSweetspot()` が speakers と
 *  floor zone の幾何から導出する。半径内はグラデーションで「中心ほど音が良い」
 *  体感を audio 側 (= live.ts setSweetspotStrength) に反映する。 */
export interface Sweetspot {
  x: number;
  y: number;
  /** sweetspot の影響半径 (= ここまでが strength > 0)。中心は strength 1.0。 */
  radius: number;
}

export interface MapDef {
  world: { w: number; h: number };
  obstacles: Obstacle[];
  speakers: Speaker[];
  zones: Zone[];
  /** 中央上部のスクリーン (= 視覚装飾。動画 streaming は別の機能で接続) */
  screens: Screen[];
  /** 固定位置 NPC (= バーテンダーなど。peer とは別、移動・通信なし) */
  npcs: NPC[];
  /** 装飾オブジェクト (= バーカウンター上のグラスなど。当たり判定なし) */
  decorations: Decoration[];
  ambience: Ambience;
}

/** バー背面棚の bottle decoration を等間隔で生成するヘルパ。
 *  ドアや bartender 位置との衝突は呼び出し側で xStart/xEnd を分割して避ける。 */
function bottleRow(y: number, xStart: number, xEnd: number, step: number): Decoration[] {
  const out: Decoration[] = [];
  for (let x = xStart; x <= xEnd; x += step) {
    out.push({ type: "bottle", x, y });
  }
  return out;
}

export const MAP: MapDef = {
  // world.h を 1280 → 1900 に拡張 (= +620px 下方向)。バーの下に
  // 「細長いエントランス通路 + キャッシャーカウンター + spawn」を追加。
  // 注意: `src/worker/club.ts` の `WORLD_H` と SPAWN_X/Y を必ず同期させる。
  //
  // 配置 (上→下):
  //   y=  0..   245 : SCREEN (上端、装飾)
  //   y=320..   440 : DJ booth zone (booth desk: y=340..390)
  //   y=440..   895 : DANCE FLOOR zone (h=455、sweetspot 中心 y=667)
  //                    speakers の collision で y=488..512 が削れる分、上下に広げて
  //                    実効踊れる y 範囲を確保。floor↔bar 壁 (y=895) ぎりぎりまで使う。
  //   y=895..   925 : floor↔bar の壁 (= ドア x=740..860 / 120px幅)
  //   y=925..  1010 : bar zone 上部 (= バーテンダー作業スペース、カウンター裏)
  //   y=1010.. 1035 : BAR COUNTER 天板 (= ドアから一直線に見える「カウンター越し」絵)
  //   y=1035.. 1280 : bar zone 下部 (= 客の歩行スペース、bar 全体は y=925..1280)
  //   y=1280.. 1310 : bar↔通路の壁 (= ドア x=700..900 / 200px幅 = 通路と同幅)
  //   y=1310.. 1900 : ENTRANCE 通路 + spawn (x=700..900 / 200px幅、左右に壁)
  //                    y=1790.. 1810: キャッシャーカウンター (中央 80px gap)
  //                    y=1860 付近 : spawn (= 入室直後はここに出現)
  world: { w: 1600, h: 1900 },
  obstacles: [
    // DJ ブースの机 (= 6 機材分のスペース: Thorens × 2 + CDJ × 2 + UREI 1620 + dope.real isolator)。
    // 旧 200×50 では足りないので 500×90 に拡張。x=550..1050 で booth zone (= 540..1060) に収まる。
    { x: 550, y: 330, w: 500, h: 90, color: 0x2a2a2a },
    // ───── サブウーファー × 2 (= Paradise Garage 流の床置き、bar 側壁の手前) ─────
    // decorations の "sub_speaker" (dec.y=850 を前縁とした 80×60 視覚) と **完全一致**
    // させるため、物理 body は y=790..850 (= dec.y - 60 .. dec.y)。旧 y=820..880 は
    // 視覚の下に 30px ずれてた (Gemini high 対応)。
    { x: 260,  y: 790, w: 80, h: 60, color: 0x1a1a1f },
    { x: 1260, y: 790, w: 80, h: 60, color: 0x1a1a1f },
    // ───── フロアとバーを仕切る壁 (横幅全部) + 中央ドア ─────
    // 壁 y=895..925 (= 30px の厚み、物理 body)。中央 x=740..860 (= 120px幅) がドア。
    // 通過すると floor zone → bar zone に切替 (zone audio は連続ブレンド)。
    // **frontDepth=60**: 視覚的な「前面」(壁の下に伸びる darker 帯) を 60px に深く
    // することで、バーから見上げて「奥にそびえる高い壁」感を演出する。物理 body
    // (= 当たり判定) は h=30 のままなので、bartender 作業スペース (y=925..1010) は
    // 維持される。bottle 装飾は壁の前面の中に並べて「棚に瓶が並んでる」絵を作る。
    { x: 0,   y: 895, w: 740, h: 30, color: 0x3a3a44, frontDepth: 60 },
    { x: 860, y: 895, w: 740, h: 30, color: 0x3a3a44, frontDepth: 60 },
    // ───── バーカウンター (バーエリア上部、壁側、横幅大) ─────
    // ドア (x=740..860) を抜けると 75px 先 (y=1010) にカウンターが見える絵。
    // カウンターは **フロアドアと整列した中央 gap** + 左右各 1 gap の合計
    // 3 gap 構成 (= 旧 2 gap 構成は中央セクションがフロア入り口の動線を塞いでいた
    // ため、ドア真下に gap を切ってエントランス→バー→フロアの真っ直ぐな導線を作る)。
    // 中央セクションを 2 分割した結果、bartender 配置は左/中/右の天板に乗せたまま、
    // 客は中央 gap を素通りしてフロアドアまで真っ直ぐ進める。
    { x: 60,   y: 1010, w: 460, h: 25, color: 0x5a3a1a }, // 左 (60..520)
    { x: 620,  y: 1010, w: 120, h: 25, color: 0x5a3a1a }, // 中央左 (620..740)
    { x: 860,  y: 1010, w: 120, h: 25, color: 0x5a3a1a }, // 中央右 (860..980)
    { x: 1080, y: 1010, w: 460, h: 25, color: 0x5a3a1a }, // 右 (1080..1540)
    // gap A: x=520..620 (100px) / gap B: x=740..860 (120px = フロアドア整列) / gap C: x=980..1080 (100px)
    // ───── バーと通路を仕切る壁 + 中央ドア (= 通路 200px と同幅) ─────
    { x: 0,   y: 1280, w: 700, h: 30, color: 0x3a3a44 },
    { x: 900, y: 1280, w: 700, h: 30, color: 0x3a3a44 },
    // ───── 通路の左右壁 (= 細長い縦通路を作る) ─────
    // 高さ 590px = y=1310..1900 まで縦に伸ばす。通路の幅は x=700..900 の 200px。
    { x: 0,   y: 1310, w: 700, h: 590, color: 0x2a2a32 },
    { x: 900, y: 1310, w: 700, h: 590, color: 0x2a2a32 },
    // ───── キャッシャーカウンター (= 通路内、spawn のちょっと上に配置) ─────
    // 中央 80px (x=760..840) を gap として残し、通路を抜けられるようにする。
    // gap が無いと spawn 直後にカウンターに塞がれて先に進めなくなる。
    { x: 700, y: 1790, w: 60, h: 25, color: 0x5a3a1a }, // 左
    { x: 840, y: 1790, w: 60, h: 25, color: 0x5a3a1a }, // 右
  ],
  speakers: [
    // 左右の壁ぴったりに巨大スタックを置く (= 描画は scene 側で sp.y を足元として
    //  上方向に伸びる)。y は床の **中央高さ** に置いて、スタックが画面の上半分
    //  (= 奥に位置する印象) を占めるようにする。下半分はフロアの自由スペース。
    //  以前は y=760 (床の底寄り) で「下に張り付いた」印象を与えていた。
    { id: "L", x: 70, y: 500, label: "L Stack" },
    { id: "R", x: 1530, y: 500, label: "R Stack" },
    // 壁側環境音スピーカー (= フルレンジ source の mono mix を距離 delay 付きで送る)。
    // 「フロアの空気感」を増す目的で、左右奥隅 + 中央前 (= booth 下) に追加配置。
    // L/R 巨大スタックを「メイン PA」、BL/BR/C を「環境音スピーカー」と捉える設計。
    // channel: ChannelSplitter での割当は live.ts 側で speaker.id "L"/"R" のみ
    // 個別 channel になり、それ以外は ChannelMerger 経由の mono mix が流れる。
    // BL/BR/C は audio fan-out 用の virtual speaker (= scene の drawSpeakers が
    // 描画 skip する。bar / DJ booth / screen と視覚的に被るので)
    { id: "BL", x: 200, y: 830, label: "BL Amb", visible: false },
    { id: "BR", x: 1400, y: 830, label: "BR Amb", visible: false },
    { id: "C", x: 800, y: 180, label: "C Amb", visible: false },
  ],
  zones: [
    {
      // フロア本体。booth の下端 (y=440) から bar の上 (y=895) ぎりぎりまで縦に広げる。
      // h=420 → 455 (+35px)。speakers (L/R) の collision で y=488..512 が約 24px 削れる
      // ので、踊れる y 範囲は 455-24=431 で実効的に旧 420 より広い。sweetspot 中心 y は
      // (440+895)/2 = 667.5、旧 670 と知覚上ほぼ同じ。
      name: "floor",
      shape: { type: "rect", x: 140, y: 440, w: 1320, h: 455 },
      audio: { gain: 1.0, lowpassHz: 18000, reverbWet: 0.18 },
    },
    {
      // DJ ブース (= 機材詰め込み版 desk 500×90 + 周囲余白)
      name: "booth",
      shape: { type: "rect", x: 540, y: 320, w: 520, h: 130 },
      audio: { gain: 1.4, lowpassHz: 20000, reverbWet: 0.08 },
      visual: { fill: 0xffd700, alpha: 0.05, label: "DJ" },
    },
    {
      // バーエリア (= 壁の下、横全幅、深さ 355px)。ドアを抜けると音が
      // こもる + 音量が下がる。zone 切替は live.ts の TWEEN_SEC で線形補間。
      name: "bar",
      shape: { type: "rect", x: 0, y: 925, w: 1600, h: 355 },
      audio: { gain: 0.35, lowpassHz: 700, reverbWet: 0.04 },
      visual: { fill: 0x2c3e50, alpha: 0.18, label: "BAR" },
    },
    {
      // エントランス通路 (= バーと spawn の間、横 200px の細長い空間)。
      // bar より更に音が小さく、lowpass も低くて「ほぼ低音だけ聞こえる」体感。
      //   gain 0.15  → bar の 0.35 から更に半分以下
      //   lowpassHz 250 → kick / bass 帯域のみ通す。中高域は -24dB/oct で減衰
      //   reverbWet 0.02 → ほぼ dry。狭い通路の鳴り感
      // 通路を進むほど音楽がドアの向こうから聞こえてくる体感が出る。
      name: "entrance",
      shape: { type: "rect", x: 0, y: 1310, w: 1600, h: 590 },
      audio: { gain: 0.15, lowpassHz: 250, reverbWet: 0.02 },
      visual: { fill: 0x1a1d24, alpha: 0.45, label: "ENTRANCE" },
    },
  ],
  screens: [
    // 中央上部のスクリーン (= 16:9 維持で控えめサイズ。speakers / booth と
    //  視覚的に被らないよう 400x225 程度に絞る。x=600 で 中央 x=800 を維持)
    { id: "main", x: 600, y: 20, w: 400, h: 225, label: "SCREEN" },
  ],
  npcs: [
    // バーテンダー × 3 (= カウンター裏 = カウンター上端 y=1010 のすぐ奥)。
    // NPC は足元基準 (origin (0.5, 1.0)) なので y=1005 (= 天板上面の 5px 上) に
    // 置くと、上半身がカウンター天板の上にニョキッと出る絵になる。
    // 中央 bartender (旧 x=800) はフロアドア整列 gap (x=740..860) に立ってしまうので
    // 中央左セクション (x=620..740) のセンター x=680 に寄せる。
    { id: "bartender_l", x: 280, y: 1005, avatarKey: "f01", label: "Bartender" },
    { id: "bartender_c", x: 680, y: 1005, avatarKey: "m01", label: "Bartender" },
    { id: "bartender_r", x: 1320, y: 1005, avatarKey: "f01", label: "Bartender" },
    // キャッシャー (= 入場担当)。通路下部のキャッシャーカウンター裏。
    // counter は y=1790..1815、NPC は y=1785 (= 天板の少し上 = 顔が見える)。
    { id: "cashier", x: 800, y: 1785, avatarKey: "f01", label: "Cashier" },
  ],
  decorations: [
    // ───── サブウーファー (= dance floor 後方、bar 側壁の手前に左右 1 発ずつ) ─────
    // obstacles に同位置 / 同寸 (80×60) で物理 body を別途持たせている。
    // 視覚: 重い dark wood box + 大型 woofer。低域 energy で同心円 ring が広がる
    // reactive wave effect の発信源。
    { type: "sub_speaker", x: 300,  y: 850 },
    { type: "sub_speaker", x: 1300, y: 850 },
    // ───── 中央上部に吊り下げた tweeter array (= L/R stack 間、HF を降らせる) ─────
    // 5 個の小型 horn を弧状に配置。天井吊りなので obstacle なし。depth は world.h+100
    // で player より絶対に手前。
    { type: "tweeter_array", x: 800, y: 220 },
    // バーカウンター天板上のグラス (= 装飾、当たり判定なし)。
    // 天板は y=1010..1035。グラスは y=1003 に並べる (= 天板の上 + バーテンダー
    // 足元と被らない位置)。3 つのカウンターセクションに分けて配置。
    { type: "glass", x: 100,  y: 1003 },
    { type: "glass", x: 200,  y: 1003 },
    { type: "glass", x: 300,  y: 1003 },
    { type: "glass", x: 400,  y: 1003 },
    { type: "glass", x: 480,  y: 1003 },
    // 旧中央 (620..980) を 2 分割して中央に gap を開けたので、gap (x=740..860) に
    // 乗る位置のグラスは省く (= x=760 を削除)。
    { type: "glass", x: 660,  y: 1003 },
    { type: "glass", x: 880,  y: 1003 },
    { type: "glass", x: 940,  y: 1003 },
    { type: "glass", x: 1120, y: 1003 },
    { type: "glass", x: 1220, y: 1003 },
    { type: "glass", x: 1320, y: 1003 },
    { type: "glass", x: 1420, y: 1003 },
    { type: "glass", x: 1500, y: 1003 },
    // ───── バーの背面棚 (= floor 側の壁の前面に 2 段で敷き詰めた酒瓶) ─────
    // 配置: floor↔bar の壁 (frontDepth=60 で y=925..985 まで見える前面) の中に、
    //   上段 y=940 / 下段 y=970 の 2 ラインで密に並べる。
    // bartender 本体は sprite が足元 (y=1005) origin、頭は y=950 付近。bottle 全部
    // (depth=dec.y ≤ 970 < bartender depth=1005) は bartender 真後ろに自然 sort され、
    // bartender 前面の x 範囲だけ瓶が隠れる「人が棚の前に立ってる」絵になる。
    // 横方向は 25px 間隔 (= ドア x=740..860 は除く)。色は scene.ts 側で (x + y*7)
    // から決定論的に 6 色パレットを循環。
    // 上段: y=945 (= 壁前面 y=925..985 の中、neck top が y=927 で wall に完全に収まる)
    ...bottleRow(945, 80, 720, 25),
    ...bottleRow(945, 880, 1520, 25),
    // 下段: y=970 (= 壁前面の下寄り。上段と x を 12.5 ずらして互い違いに密度を出す)
    ...bottleRow(970, 92, 720, 25),
    ...bottleRow(970, 892, 1520, 25),
    // 通路の床に上向き矢印を等間隔で並べる (= spawn から bar に向かう方向案内)。
    // 通路 x=700..900 の中央 x=800、y=1370 から y=1750 まで ~80px 間隔。
    { type: "arrow_up", x: 800, y: 1750 },
    { type: "arrow_up", x: 800, y: 1670 },
    { type: "arrow_up", x: 800, y: 1590 },
    { type: "arrow_up", x: 800, y: 1510 },
    { type: "arrow_up", x: 800, y: 1430 },
    { type: "arrow_up", x: 800, y: 1370 },
  ],
  ambience: {
    // IR を入れたら "/ir/club.wav" 等
    irUrl: undefined,
    airAbsorption: true,
    // 200 → 400 → 800: line-array PA は audience に向けて aim されているため、
    // 物理的な空気吸収より frontal HF 維持率が高い。距離による高域 cutoff の
    // 差を圧縮して、X 軸移動で L/R 間に高域チルトが立つ違和感を更に抑える。
    airAbsorptionRef: 800,
    airAbsorptionMaxHz: 18000,
    // 旧 800 → 2500 → 6000: X 軸移動で L/R speaker の lowpass cutoff が大きく
    // 動くと「ステレオ像が左右に偏る」体感になる。minHz を高めに置いて、最遠
    // speaker でも 6kHz は残るようにし、L/R 間の高域差を聴感上ほぼ気にならない
    // 範囲に抑える。realistic な距離吸収より「クラブで自然に聞こえる」を優先。
    airAbsorptionMinHz: 6000,
    // 旧 0.6 → 0.3: 人数増加での低音減衰を半減
    crowdLowCut: { perPersonDb: 0.3 },
    // floor zone の reverbWet=0.18 より少し控えめ。scene が tick するまでの
    // 数フレームを「ドライではない最低限の空気感」で埋める
    defaultReverbWet: 0.1,
  },
};

/** 音響的に最も良い聴取位置を speakers と floor zone から導出する。
 *  - 中心 x: visible (= 物理 PA) speakers の centroid。実装上は左右壁の L/R が
 *    候補で、x = (Lx + Rx) / 2 が stereo image の中心になる。
 *  - 中心 y: "floor" zone の rect 中央 (= dance area の真ん中)。speakers の手前
 *    すぎず、bar 側の壁にも近すぎない。
 *  - radius: floor zone の縦幅の半分弱 (= dance floor 全面をほぼカバー)。
 *    天井に吊られた tweeter array が HF を dance floor 全面に降らせる物理現実を
 *    モデル化したもの。実機 Paradise Garage / The Loft が「踊ってる場所どこでも
 *    sweet spot」と言われた所以を audio 的に再現する。
 *    sweet spot 外 = 壁際 / bar zone / 隅 だけが「クリア度が落ちる」エリアになる。
 *    具体値: floor h=455 → radius=floor.h/2 - 30 ≈ 200 で中心から縁まで gradient。
 *  speakers/zones を編集したら自動で位置 + 半径が追従する (= ハードコード値ゼロ)。 */
export function computeSweetspot(map: MapDef): Sweetspot {
  const main = map.speakers.filter((s) => s.visible !== false);
  const cx = main.length > 0
    ? main.reduce((s, sp) => s + sp.x, 0) / main.length
    : map.world.w / 2;
  const floor = map.zones.find((z) => z.name === "floor");
  const cy = floor && floor.shape.type === "rect"
    ? floor.shape.y + floor.shape.h / 2
    : map.world.h / 2;
  // radius: floor の縦幅の半分弱。floor が見つからなければ最低 200。
  const radius = floor && floor.shape.type === "rect"
    ? Math.max(100, floor.shape.h / 2 - 30)
    : 200;
  return { x: cx, y: cy, radius };
}

/** MAP から computeSweetspot で導出した sweet spot 座標 (= 描画・strength 計算で使用)。 */
export const SWEETSPOT: Sweetspot = computeSweetspot(MAP);

export function rectContains(shape: Shape, x: number, y: number): boolean {
  return x >= shape.x && x <= shape.x + shape.w && y >= shape.y && y <= shape.y + shape.h;
}

// 重なる場合は配列の後ろほど「具体的」 (= 優先される) という仕様。
// floor を一番上に書いて bar / booth を後に書くと「bar に居れば bar、それ以外は floor」になる。
export function findActiveZone(zones: Zone[], x: number, y: number): Zone | null {
  let active: Zone | null = null;
  for (const z of zones) {
    if (rectContains(z.shape, x, y)) active = z;
  }
  return active;
}
